import { maximumSupplementaryLayouts } from "@tamariba/drills";
import type { DrillWrite } from "./types";

export const layoutDraftPrefix = "tamariba.drill-layout-draft:";
export const layoutDraftLifetime = 2 * 60 * 60 * 1000;
// Preserve 31 descriptions and video URLs, including the form's other fields.
export const maximumLayoutDraftLength = 250000;
const uuid = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;
export const validDraftId = (value: unknown): value is string =>
  typeof value === "string" && uuid.test(value);

export type LayoutDraft = {
  version: 1;
  createdAt: number;
  drillId: string | null;
  form: DrillWrite;
  creationId: string;
  dirty: boolean;
  activeIndex?: number;
  // The returning layout is appended, or replaces this position.
  replaceIndex: number | null;
};

export function currentLayoutDetails(form: DrillWrite) {
  return [form.layoutId, ...(form.supplementaryLayoutIds ?? [])]
    .filter(Boolean)
    .map(
      (layoutId) =>
        form.layoutDetails?.find((detail) => detail.layoutId === layoutId) ?? {
          layoutId,
          description: "",
          videoUrl: "",
        },
    );
}

// This changes only the selected layout IDs. Descriptions, videos and other
// unsaved drill fields remain intact, including when an existing layout returns.
export function restoreLayoutDraft(
  draft: LayoutDraft,
  returnedLayoutId?: string,
): { form: DrillWrite; activeIndex: number } {
  const form = draft.form;
  const supplementary = form.supplementaryLayoutIds ?? [];
  if (
    !Array.isArray(supplementary) ||
    supplementary.length > maximumSupplementaryLayouts ||
    !supplementary.every(validDraftId) ||
    (form.layoutId !== "" && !validDraftId(form.layoutId)) ||
    (form.layoutId === "" && supplementary.length > 0)
  )
    throw new Error("配置図の情報を復元できません。");
  const ids = [form.layoutId, ...supplementary].filter(Boolean);
  if (new Set(ids.map((id) => id.toLowerCase())).size !== ids.length)
    throw new Error("同じ配置図は重複して登録できません。");
  if (
    draft.replaceIndex !== null &&
    (!Number.isInteger(draft.replaceIndex) ||
      draft.replaceIndex < 0 ||
      draft.replaceIndex > Math.max(0, ids.length - 1))
  )
    throw new Error("配置図の位置を復元できません。");
  const activeIndex = draft.activeIndex ?? draft.replaceIndex ?? 0;
  if (
    !Number.isInteger(activeIndex) ||
    activeIndex < 0 ||
    activeIndex > ids.length
  )
    throw new Error("配置図の位置を復元できません。");
  if (returnedLayoutId === undefined) return { form, activeIndex };
  if (!validDraftId(returnedLayoutId))
    throw new Error("戻り先の配置図IDが不正です。");
  const found = ids.findIndex(
    (id) => id.toLowerCase() === returnedLayoutId.toLowerCase(),
  );
  if (found >= 0) return { form, activeIndex: found };
  const position = draft.replaceIndex ?? ids.length;
  if (position === ids.length && ids.length >= maximumSupplementaryLayouts + 1)
    throw new Error(
      `サブ配置図は${maximumSupplementaryLayouts}枚まで登録できます。`,
    );
  ids.splice(position, draft.replaceIndex === null ? 0 : 1, returnedLayoutId);
  return {
    form: {
      ...form,
      layoutId: ids[0] ?? "",
      supplementaryLayoutIds: ids.slice(1),
    },
    activeIndex: position,
  };
}

export function parseLayoutDraft(
  raw: string | null,
  context: { collectionId: string; drillId?: string },
  now = Date.now(),
): LayoutDraft | null {
  if (!raw || raw.length > maximumLayoutDraftLength) return null;
  try {
    const draft = JSON.parse(raw) as LayoutDraft;
    const form = draft.form;
    if (
      draft.version !== 1 ||
      !Number.isFinite(draft.createdAt) ||
      now - draft.createdAt < 0 ||
      now - draft.createdAt > layoutDraftLifetime ||
      draft.drillId !== (context.drillId ?? null) ||
      !validDraftId(draft.creationId) ||
      typeof draft.dirty !== "boolean" ||
      !form ||
      form.collectionId !== context.collectionId ||
      !validDraftId(form.collectionId) ||
      (form.layoutId !== "" && !validDraftId(form.layoutId)) ||
      !Array.isArray(form.supplementaryLayoutIds) ||
      form.supplementaryLayoutIds.length > maximumSupplementaryLayouts ||
      !form.supplementaryLayoutIds.every((id) => validDraftId(id)) ||
      !Number.isInteger(form.revision) ||
      form.revision < 0
    )
      return null;
    if (
      ![form.title, form.goal, form.memo, form.videoUrl].every(
        (value) => typeof value === "string",
      ) ||
      !(
        form.nextPracticeOn === null || typeof form.nextPracticeOn === "string"
      ) ||
      !Array.isArray(form.references) ||
      form.references.length > 10 ||
      !form.references.every(
        (reference) =>
          reference &&
          [reference.title, reference.note, reference.videoUrl].every(
            (value) => typeof value === "string",
          ),
      )
    )
      return null;
    if (
      form.skillIds !== undefined &&
      (!Array.isArray(form.skillIds) ||
        form.skillIds.length > 50 ||
        !form.skillIds.every(validDraftId) ||
        new Set(form.skillIds.map((id) => id.toLowerCase())).size !==
          form.skillIds.length)
    )
      return null;
    if (
      !Array.isArray(form.layoutDetails) ||
      form.layoutDetails.length > maximumSupplementaryLayouts + 1 ||
      !form.layoutDetails.every(
        (detail) =>
          detail &&
          validDraftId(detail.layoutId) &&
          typeof detail.description === "string" &&
          typeof detail.videoUrl === "string",
      )
    )
      return null;
    if (
      draft.replaceIndex !== null &&
      (!Number.isInteger(draft.replaceIndex) ||
        draft.replaceIndex < 0 ||
        draft.replaceIndex > form.supplementaryLayoutIds.length)
    )
      return null;
    const ids = [form.layoutId, ...form.supplementaryLayoutIds].filter(Boolean);
    if (
      (form.layoutId === "" && form.supplementaryLayoutIds.length > 0) ||
      new Set(ids.map((id) => id.toLowerCase())).size !== ids.length ||
      (draft.activeIndex !== undefined &&
        (!Number.isInteger(draft.activeIndex) ||
          draft.activeIndex < 0 ||
          draft.activeIndex > ids.length))
    )
      return null;
    return draft;
  } catch {
    return null;
  }
}
